/**
 * Plant-o-Pedia Shared Utilities
 * Phase 1.1 - Centralized source of truth
 */

/**
 * Detects if a string is a URL or a specific upload path.
 * Used for sanitizing data and avoiding rendering URLs as text.
 */
export const checkIsUrlString = (str: any): boolean => 
  typeof str === 'string' && (
    str.startsWith('http') || 
    str.includes('://') || 
    str.startsWith('/uploads/') ||
    str.startsWith('blob:') ||
    str.includes('.jpg') ||
    str.includes('.png')
  );

/**
 * Translates plant category to Czech.
 */
export const translateCategory = (cat: string | undefined): string => {
  if (!cat) return 'Neznámá';
  const mapping: Record<string, string> = {
    vegetable: 'Zelenina',
    herb: 'Bylinka',
    fruit: 'Ovoce',
    flower: 'Květina',
    tree: 'Strom',
    shrub: 'Keř',
    grass: 'Tráva',
    succulent: 'Sukulent',
    climber: 'Popínavá',
    houseplant: 'Pokojová',
    mushroom: 'Houba',
    other: 'Ostatní'
  };
  return mapping[cat.toLowerCase()] || cat;
};

/**
 * Gets color based on severity levels (1-5).
 */
export const getSeverityColor = (level: number): string => {
  if (level >= 5) return 'red';
  if (level >= 4) return 'orange';
  if (level >= 3) return 'yellow';
  if (level >= 2) return 'cyan';
  return 'green';
};

import i18n from '../../../i18n';

/**
 * Formats a period array (numbers 1-12 or strings) into a localized comma-separated string.
 */
export const formatPeriod = (p: any): string | null => {
  if (!p) return null;
  const arr = Array.isArray(p) ? p : [p];
  const filtered = arr.filter(v => v !== null && v !== undefined);
  if (filtered.length === 0) return null;

  // Handle number arrays (months 1-12)
  if (typeof filtered[0] === 'number') {
    const language = i18n.language || 'cs';
    const formatter = new Intl.DateTimeFormat(language, { month: 'long' });
    return (filtered as number[])
      .sort((a, b) => a - b)
      .map(m => {
        const date = new Date(2024, m - 1, 1);
        return formatter.format(date);
      })
      .join(', ');
  }

  // Handle string arrays (legacy)
  const valid = (filtered as string[]).filter(v => typeof v === 'string' && v.trim() !== '');
  return valid.length > 0 ? valid.join(', ') : null;
};

/**
 * Labels and Constants
 */
export const DIFFICULTY_LABELS: Record<number, string> = {
  1: 'Triviální',
  2: 'Snadné',
  3: 'Střední',
  4: 'Náročné',
  5: 'Expert'
};

export const WATER_LABELS: Record<number, string> = {
  1: 'Minimální',
  2: 'Nízká',
  3: 'Střední',
  4: 'Vysoká',
  5: 'Velmi vysoká'
};

export const NUTRIENT_LABELS: Record<number, string> = {
  1: 'Nízké',
  2: 'Mírné',
  3: 'Střední',
  4: 'Vysoké',
  5: 'Velmi vysoké'
};

export const CATEGORY_LABELS: Record<string, string> = {
  vegetable: 'Zelenina',
  herb: 'Bylinka',
  fruit: 'Ovoce',
  flower: 'Květina',
  tree: 'Strom',
  shrub: 'Keř',
  grass: 'Tráva',
  succulent: 'Sukulent',
  climber: 'Popínavá',
  houseplant: 'Pokojová',
  mushroom: 'Houba',
  other: 'Ostatní'
};

/**
 * Translates difficulty level to text label.
 */
export const getDifficultyLabel = (level: number): string => {
  return DIFFICULTY_LABELS[level] || String(level);
};

/**
 * Gets a human-readable label for companion net score.
 */
export const getCompanionLabel = (net: number): string => {
  if (net >= 0.8) return 'Výborný';
  if (net >= 0.4) return 'Dobrý';
  if (net >= 0.1) return 'Vhodný';
  if (net > -0.1) return 'Neutrální';
  if (net > -0.4) return 'Nevhodný';
  if (net > -0.8) return 'Špatný';
  return 'Kritický';
};

/**
 * Gets a text color class for companion human label.
 */
export const getCompanionLabelColor = (net: number): string => {
  if (net >= 0.4) return 'text-green-600 dark:text-green-400';
  if (net >= 0.1) return 'text-lime-600 dark:text-lime-400';
  if (net > -0.1) return 'text-yellow-600 dark:text-yellow-400';
  if (net > -0.4) return 'text-orange-600 dark:text-orange-400';
  return 'text-red-600 dark:text-red-400';
};
